import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import { Contact, ContactType } from "@akfdev-com/shared";
import { contactsApi } from "../../services/api";
import { ContactsToolbar } from "./ContactsToolbar";
import { ContactListView } from "./ContactListView";
import { ContactGridView } from "./ContactGridView";

/**
 * Contacts List Page
 *
 * Renders the contacts directory with:
 * - Toolbar with search, type filter, balance filter, List/Grid toggle
 * - Dynamic view rendering (list or grid based on toggle)
 * - URL-based query parameters for routing (search, type, balance, view)
 * - Search via backend API (contactsApi.getAll with params)
 * - Client-side balance filtering (owed/owing)
 *
 * Validates: Requirements 1.1, 1.3, 1.4, 1.5
 */
const ContactsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL params
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [type, setType] = useState<ContactType | "all">(
    (searchParams.get("type") as ContactType | "all") || "all",
  );
  const [balance, setBalance] = useState<"all" | "owed" | "owing">(
    (searchParams.get("balance") as "all" | "owed" | "owing") || "all",
  );
  const [view, setView] = useState<"list" | "grid">(
    (searchParams.get("view") as "list" | "grid") || "list",
  );

  // Data state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch contacts when search/type changes
  useEffect(() => {
    const fetchContacts = async () => {
      try {
        setLoading(true);
        const params: Record<string, any> = {};
        if (search) params.search = search;
        if (type && type !== "all") params.type = type;

        const data = await contactsApi.getAll(params);
        setContacts(Array.isArray(data) ? data : []);
      } catch {
        setError("Failed to load contacts. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [search, type]);

  // Update URL params when state changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (type && type !== "all") params.set("type", type);
    if (balance && balance !== "all") params.set("balance", balance);
    if (view && view !== "list") params.set("view", view);
    setSearchParams(params);
  }, [search, type, balance, view, setSearchParams]);

  // Apply balance filter client-side
  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      if (balance === "owed") return (c.totalOwed ?? 0) > 0;
      if (balance === "owing") return (c.totalOwing ?? 0) > 0;
      return true;
    });
  }, [contacts, balance]);

  const handleToolbarChange = (next: {
    search?: string;
    type?: ContactType | "all";
    balance?: "all" | "owed" | "owing";
    view?: "list" | "grid";
  }) => {
    if (next.search !== undefined) setSearch(next.search);
    if (next.type !== undefined) setType(next.type);
    if (next.balance !== undefined) setBalance(next.balance);
    if (next.view !== undefined) setView(next.view);
  };

  if (loading) return <Spinner message="Loading contacts..." />;
  if (error) return <ErrorAlert message={error} />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contacts"
        description="Manage residents, suppliers, and customers across your properties."
        actions={
          <button
            type="button"
            onClick={() => navigate("/dashboard/contacts/new")}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            + New Contact
          </button>
        }
      />

      {/* Toolbar */}
      <ContactsToolbar
        search={search}
        type={type}
        balance={balance}
        view={view}
        onChange={handleToolbarChange}
      />

      {/* View */}
      {filteredContacts.length === 0 ? (
        <div className="bg-white rounded-lg shadow px-6 py-12 text-center">
          <p className="text-sm text-gray-500">
            {search || type !== "all" || balance !== "all"
              ? "No contacts match your search criteria."
              : "No contacts yet."}
          </p>
          {(search || type !== "all" || balance !== "all") && (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setType("all");
                setBalance("all");
              }}
              className="mt-4 text-sm text-blue-600 hover:text-blue-700"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : view === "list" ? (
        <ContactListView contacts={filteredContacts} />
      ) : (
        <ContactGridView contacts={filteredContacts} />
      )}
    </div>
  );
};

export default ContactsList;
